import { useState } from 'react'
import { router } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { submitDownloadForm } from '@/lib/http'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Download } from 'lucide-react'

type StaffOption = {
  id: string
  email_address: string
}

type PreviewCount = {
  total: number
  business_count: number
  formation_count: number
}

type Props = {
  filters: {
    search: string
    record_type: string
    compliance_officer_id: string | null
    support_officer_id: string | null
    status: string
    created_after: string | null
    created_before: string | null
  }
  staffOptions: StaffOption[]
  previewCount: PreviewCount | null
}

export default function ExportsContacts({ filters, staffOptions, previewCount }: Props) {
  const [search, setSearch] = useState(filters.search)
  const [recordType, setRecordType] = useState(filters.record_type)
  const [complianceOfficerId, setComplianceOfficerId] = useState(filters.compliance_officer_id || 'any')
  const [supportOfficerId, setSupportOfficerId] = useState(filters.support_officer_id || 'any')
  const [status, setStatus] = useState(filters.status)
  const [createdAfter, setCreatedAfter] = useState(filters.created_after || '')
  const [createdBefore, setCreatedBefore] = useState(filters.created_before || '')
  const [preview, setPreview] = useState<PreviewCount | null>(previewCount)
  const [dialogOpen, setDialogOpen] = useState(false)

  function applyFilters() {
    const params: Record<string, string> = {
      search: search || '',
      record_type: recordType,
      status: status,
    }
    if (complianceOfficerId && complianceOfficerId !== 'any') params.compliance_officer_id = complianceOfficerId
    if (supportOfficerId && supportOfficerId !== 'any') params.support_officer_id = supportOfficerId
    if (createdAfter) params.created_after = createdAfter
    if (createdBefore) params.created_before = createdBefore
    params.preview = 'true'

    router.get('/backoffice/exports/contacts', params, {
      preserveState: true,
      preserveScroll: true,
      replace: true,
      onSuccess: (page) => {
        setPreview(page.props.previewCount as PreviewCount | null)
      },
    })
  }

  function clearFilters() {
    setSearch('')
    setRecordType('all')
    setComplianceOfficerId('any')
    setSupportOfficerId('any')
    setStatus('all')
    setCreatedAfter('')
    setCreatedBefore('')
    setPreview(null)
  }

  function handleExport() {
    setDialogOpen(false)

    submitDownloadForm('/backoffice/exports/contacts', {
      search,
      record_type: recordType,
      compliance_officer_id: complianceOfficerId === 'any' ? '' : complianceOfficerId,
      support_officer_id: supportOfficerId === 'any' ? '' : supportOfficerId,
      status,
      created_after: createdAfter,
      created_before: createdBefore,
    })
  }

  function activeFilterCount() {
    let count = 0
    if (search) count++
    if (recordType !== 'all') count++
    if (complianceOfficerId && complianceOfficerId !== 'any') count++
    if (supportOfficerId && supportOfficerId !== 'any') count++
    if (status !== 'all') count++
    if (createdAfter) count++
    if (createdBefore) count++
    return count
  }

  return (
    <BackofficeLayout
      title="Export Contacts"
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Export Contacts' },
      ]}
      contentWidth="wide"
    >
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Export Customer Contacts</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Filter and export customer contact information across all businesses and formations.
          </p>
        </div>

        {/* Filters */}
        <section className="border rounded-lg p-4 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Search</label>
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Name or workspace..."
                className="h-9 text-sm"
                onKeyDown={e => e.key === 'Enter' && applyFilters()}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Record Type</label>
              <Select value={recordType} onValueChange={setRecordType}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Records</SelectItem>
                  <SelectItem value="business">Businesses Only</SelectItem>
                  <SelectItem value="formation">Formations Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {recordType !== 'formation' && (
                    <>
                      <SelectItem value="with_formation">With Formation</SelectItem>
                      <SelectItem value="without_formation">Without Formation</SelectItem>
                    </>
                  )}
                  {recordType !== 'business' && (
                    <>
                      <SelectItem value="application">Formation — Application</SelectItem>
                      <SelectItem value="filing">Formation — Filing</SelectItem>
                      <SelectItem value="completed">Formation — Completed</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Assigned CO</label>
              <Select value={complianceOfficerId} onValueChange={setComplianceOfficerId}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Any CO" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any CO</SelectItem>
                  {staffOptions.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.email_address}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Assigned SO</label>
              <Select value={supportOfficerId} onValueChange={setSupportOfficerId}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Any SO" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any SO</SelectItem>
                  {staffOptions.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.email_address}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Created After</label>
              <Input
                type="date"
                value={createdAfter}
                onChange={e => setCreatedAfter(e.target.value)}
                className="h-9 text-sm"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Created Before</label>
              <Input
                type="date"
                value={createdBefore}
                onChange={e => setCreatedBefore(e.target.value)}
                className="h-9 text-sm"
              />
            </div>
          </div>

          {/* Action bar */}
          <div className="flex items-center justify-between pt-2 border-t">
            <div className="flex items-center gap-2">
              {activeFilterCount() > 0 && (
                <Badge variant="secondary" className="text-xs font-normal">
                  {activeFilterCount()} active filter{activeFilterCount() !== 1 ? 's' : ''}
                </Badge>
              )}
              {preview && (
                <span className="text-sm text-muted-foreground">
                  {preview.total} matching record{preview.total !== 1 ? 's' : ''}
                  {' '}({preview.business_count} business{preview.business_count !== 1 ? 'es' : ''},{' '}
                  {preview.formation_count} formation{preview.formation_count !== 1 ? 's' : ''})
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={clearFilters}>
                Clear
              </Button>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={applyFilters}>
                Preview Count
              </Button>
              <Button
                size="sm"
                className="h-8 text-xs gap-1.5"
                disabled={!preview || preview.total === 0}
                onClick={() => setDialogOpen(true)}
              >
                <Download className="w-3.5 h-3.5" />
                Export CSV
              </Button>
            </div>
          </div>
        </section>

        {/* Confirmation dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Export Customer Contacts</DialogTitle>
              <DialogDescription>
                {preview ? (
                  <>
                    This will export {preview.total} matching record{preview.total !== 1 ? 's' : ''}:{' '}
                    {preview.business_count} business{preview.business_count !== 1 ? 'es' : ''},{' '}
                    {preview.formation_count} formation{preview.formation_count !== 1 ? 's' : ''}.
                  </>
                ) : (
                  'Generating export...'
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="py-2 space-y-3 text-sm">
              <div className="text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Columns included:</p>
                <p>Record type, business/formation name, workspace, customer owner name/email, assigned CO/SO, status, creation date, record ID.</p>
              </div>
              <div className="text-amber-700 text-xs bg-amber-50 border border-amber-200 rounded px-3 py-2">
                This export contains customer contact information. Handle responsibly and in accordance with your data handling policies.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleExport}>
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Export CSV
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </BackofficeLayout>
  )
}
